import action from "./../helpers/rest-client";

export class ErrorGroup extends Error { }
class Group {
  public async exportSubscribers(groupId, filters) {
    const queryString = new URLSearchParams(filters).toString();
    action
      .Get({
        url: `/api/group/export/subscribers/${groupId}${!!queryString ? `?${queryString}` : ""}`,
      })
      .then((response) => {
        const binary = new Uint8Array(response.data);

        const buftype =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8";

        const url = window.URL.createObjectURL(
          new Blob([binary], {
            type: buftype,
          })
        );

        // Create blob link to download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Suscriptores" + ".xlsx");

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link?.parentNode?.removeChild(link);
      })
      .catch((error) => {
        return error;
      });
  }

  public async getGroupBySlug(slug: string) {
    return action
      .Get({
        url: `/api/group/name/${slug}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async getGroupLastActivity({ group_id }: { group_id: number }) {
    return action
      .Get({
        url: `/api/group/last-activity/${group_id}`,
      })
      .then((response) => {
        return { response };
      })
      .catch((error) => {
        return { error };
      });
  }

  public async getGroupById({ group_id }) {
    return action
      .Get({
        url: `/api/group/id/${group_id}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async getTotalSubscribers({ group_id }) {
    return action
      .Get({
        url: `/api/group/total-subscribers/${group_id}`,
      })
      .then((response) => {
        return { totalSubscribers: response };
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async getSubscribers({ id, page, filters }) {
    const queryString = new URLSearchParams(filters).toString();

    return action
      .Get({
        url: `/api/group/subscribers/${id}?page=${!!page ? page : 1}${!!queryString ? `&${queryString}` : ""
          }`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async getSubscribersByPlan({
    group_id,
    plans_period_unit,
    plan_period,
    plan_type,
    page,
    per_page,
    current_amounts,
    sort_by,
  }: {
    group_id: string | number;
    plans_period_unit: string | null;
    plan_period: number | null;
    plan_type?: string;
    page?: number;
    per_page?: number;
    current_amounts?: number[];
    sort_by?: string;
  }) {
    const query = new URLSearchParams({
      ...(plans_period_unit !== null ? { plans_period_unit } : {}),
      ...(plan_period !== null ? { plan_period: String(plan_period) } : {}),
      ...(plan_type ? { plan_type } : {}),
      ...(page ? { page: String(page) } : {}),
      ...(per_page ? { per_page: String(per_page) } : {}),
      ...(current_amounts && current_amounts.length > 0 ? { current_amounts: current_amounts.join(",") } : {}),
      ...(sort_by ? { sort_by } : {}),
    }).toString();

    return action
      .Get({
        url: `/api/group/subscribers-by-plan/${group_id}?${query}`,
      })
      .then((response) => ({ response }))
      .catch((error) => ({ error }));
  }

  public async updateSubscribersPrice({ group_id, payload }) {
    return action
      .Post({
        url: `/api/group/subscribers-price-update/${group_id}`,
        body: payload,
      })
      .then((response) => ({ response }))
      .catch((error) => ({ error }));
  }

  public async getSubscriberTelegramGroup(group_id: string) {
    const response: string = await action
      .Get({
        url: `/api/group/telegram/subscriber/${group_id}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });

    return response;
  }

  public async getGroupMetrics({ id, filters }) {
    const queryString = new URLSearchParams(filters).toString();

    return action
      .Get({
        url: `/api/group/metrics/${id}${!!queryString ? `?${queryString}` : ""}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }

  public async getExpertTelegramGroup(group_id: string) {
    const response: string = await action
      .Get({
        url: `/api/group/telegram/expert/${group_id}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });

    return response;
  }

  public async getExpertTelegramBotUrlForNotifications() {
    const response: string = await action
      .Get({
        url: `/api/group/telegram/expertbot-url`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });

    return response;
  }

  public async newTelegramGroup(payload) {
    const response: string = await action
      .Post({
        url: `/api/group/telegram/`,
        body: payload,
        config: { timeout: 60000 * 5 },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });

    return response;
  }

  public async deleteReview(review_id: number) {
    const response: string = await action
      .Del({
        url: `/api/group/review/${review_id}`,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });

    return response;
  }

  public async newGroup(payload) {
    return action
      .Post({
        url: "/api/group",
        body: {
          data: JSON.stringify(payload),
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async updateGroup(payload) {
    const { id, ...rest } = payload;

    return action
      .Put({
        url: "/api/group",
        body: {
          data: JSON.stringify(rest),
          id
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async updateGroupMainCurrency(payload) {
    return action
      .Put({
        url: "/api/group/main-currency",
        body: payload,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response.statusText);
      });
  }

  public async cancelScheduleTasks(taskIds: number[]) {
    // Cancelar todas las schedule tasks en paralelo
    const promises = taskIds.map((taskId) =>
      action.Put({
        url: `/api/schedule-tasks/${taskId}`,
        body: {
          data: {
            status: 'cancelled'
          }
        }
      })
    );

    return Promise.all(promises)
      .then((responses) => {
        return responses;
      })
      .catch((error) => {
        throw new ErrorGroup(error.response?.statusText || error.message);
      });
  }
}

const group = new Group();

export default group;
